import { useEffect, useRef, useState } from "react";
import { Stack, Text, View, XStack, YStack } from "tamagui";
import { RiCheckLine } from "react-icons/ri";
import { appToast } from "@components/Toast";
import { colors } from "@styles/colors";
import { handleAPIRegisterPaymentMethod } from "@screen/Home/pages/Account/API/handlePaymentAPI";
import { TAPPAY_SERVER_TYPE } from "@utils/tappaySdk";
import { TAPPAY_LOGO_URL } from "@components/payment/TapPayCompliance";

const TAPPAY_SCRIPT_URL = process.env.REACT_APP_TAPPAY_SCRIPT_URL || "";
const APP_ID = Number(process.env.REACT_APP_TAPPAY_APP_ID) || 0;
const APP_KEY = process.env.REACT_APP_TAPPAY_APP_KEY || "";

// 與 Kiosk AddCard（kiosk-card-*）不同的 element id：TapPay Fields 同頁不能掛兩組同名元素。
const IDS = {
  number: "gas-inline-card-number",
  exp: "gas-inline-card-expiration-date",
  ccv: "gas-inline-card-ccv",
} as const;

const INK = "#0f1a35";

export type BoundCard = { payment_method_id?: string; card?: { lastfour?: string } };

/**
 * 信用卡卡面上的內嵌綁卡表單（非 Sheet）。
 * TapPay Fields 一經掛載即保留 DOM，父層只用 CSS 收合，避免反覆卸載重掛。
 */
const InlineBindCardForm = ({
  open,
  cusJwtToken,
  onBound,
  onCancel,
}: {
  open: boolean;
  cusJwtToken: string;
  onBound: (created?: BoundCard) => void;
  onCancel: () => void;
}) => {
  const [agree, setAgree] = useState(false);
  const [sdkReady, setSdkReady] = useState(false);
  const [cardStatus, setCardStatus] = useState<any>(null);
  const [submitting, setSubmitting] = useState(false);
  const inited = useRef(false);

  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | undefined;
    const tryInit = () => {
      if (inited.current) return true;
      const el = document.getElementById(IDS.number);
      if (!window.TPDirect || !el) return false;
      // 元素若被收合藏起，TapPay 有時掛不上——僅在 open 時 init
      if (!open) return false;
      inited.current = true;
      window.TPDirect.setupSDK(APP_ID, APP_KEY, TAPPAY_SERVER_TYPE);
      window.TPDirect.card.setup({
        fields: {
          number: { element: `#${IDS.number}`, placeholder: "**** **** **** ****" },
          expirationDate: { element: `#${IDS.exp}`, placeholder: "MM / YY" },
          ccv: { element: `#${IDS.ccv}`, placeholder: "CCV" },
        },
        styles: {
          input: { color: INK, "font-size": "15px", "font-weight": "700" },
          "input.valid": { color: colors.green },
          "input.invalid": { color: colors.danger },
        },
      });
      window.TPDirect.card.onUpdate((u: any) => setCardStatus(u));
      setSdkReady(true);
      return true;
    };

    const startPolling = () => {
      if (tryInit()) return;
      timer = setInterval(() => {
        if (tryInit() && timer) clearInterval(timer);
      }, 150);
    };

    if (!open) return () => timer && clearInterval(timer);

    if (window.TPDirect) {
      startPolling();
    } else if (TAPPAY_SCRIPT_URL) {
      let script = document.querySelector(`script[src="${TAPPAY_SCRIPT_URL}"]`) as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement("script");
        script.src = TAPPAY_SCRIPT_URL;
        script.async = true;
        document.body.appendChild(script);
      }
      script.addEventListener("load", startPolling, { once: true });
      startPolling();
    }
    return () => timer && clearInterval(timer);
  }, [open]);

  const canBind = agree && sdkReady && cardStatus?.canGetPrime && !submitting;

  const bind = () => {
    if (!window.TPDirect || !canBind) return;
    setSubmitting(true);
    window.TPDirect.card.getPrime(async (result: any) => {
      if (result.status !== 0) {
        setSubmitting(false);
        appToast.error(`取得卡片資訊失敗：${result.msg || "請確認卡號"}`);
        return;
      }
      const prime = result?.card?.prime;
      const last4 = result?.card?.lastfour ?? "";
      const reg = await handleAPIRegisterPaymentMethod({ method_type: "credit_card", prime, nickname: `**** **** **** ${last4}` }, cusJwtToken || "");
      setSubmitting(false);
      if (reg.state === "success") {
        // 3DS challenge：跳出到銀行驗證頁；完成後由落地頁處理導回。
        if (reg.data?.bind_status === "challenge_required" && reg.data.payment_url) {
          window.location.href = reg.data.payment_url;
          return;
        }
        appToast.success("已新增信用卡");
        onBound(reg.data?.payment_method ?? undefined);
      } else {
        const msg = Array.isArray(reg.error) ? reg.error.join("\n") : reg.error;
        appToast.error(msg || "綁卡失敗，請稍後再試");
      }
    });
  };

  return (
    <div className={`gas-pay-bind${open ? " open" : ""}`} aria-hidden={!open}>
      <div className="gas-pay-bind-inner">
        <XStack ai="center" jc="space-between" mb={8}>
          <Text fontSize={13} fontWeight="800" color="#fff">
            填寫卡片資料
          </Text>
          <Stack
            onPress={(e: any) => {
              e?.stopPropagation?.();
              onCancel();
            }}
            pressStyle={{ opacity: 0.7 }}
            style={{ cursor: "pointer" }}
            px={8}
            py={4}
          >
            <Text fontSize={12} fontWeight="700" color="rgba(255,255,255,0.75)">
              取消
            </Text>
          </Stack>
        </XStack>

        <YStack className="gas-pay-bind-field" gap={4}>
          <Text fontSize={11} fontWeight="700" color="rgba(15,26,53,0.55)">
            卡號
          </Text>
          <View id={IDS.number} h={26} />
        </YStack>

        <XStack gap={8} mt={8}>
          <YStack className="gas-pay-bind-field" flex={1} gap={4}>
            <Text fontSize={11} fontWeight="700" color="rgba(15,26,53,0.55)">
              有效期限
            </Text>
            <View id={IDS.exp} h={26} />
          </YStack>
          <YStack className="gas-pay-bind-field" width={100} gap={4}>
            <Text fontSize={11} fontWeight="700" color="rgba(15,26,53,0.55)">
              安全碼
            </Text>
            <View id={IDS.ccv} h={26} />
          </YStack>
        </XStack>

        <XStack ai="center" gap={6} mt={8} flexWrap="wrap">
          <Text fontSize={11} color="rgba(255,255,255,0.78)" flexShrink={1}>
            <img
              src={TAPPAY_LOGO_URL}
              alt="TapPay"
              style={{
                height: 22,
                width: "auto",
                display: "inline-block",
                verticalAlign: "middle",
                margin: "0 3px 2px 0",
                opacity: 0.95,
                filter: "brightness(0) invert(1)",
              }}
            />
            加密，卡號不經本商店
          </Text>
        </XStack>

        <XStack
          mt={10}
          ai="flex-start"
          gap={8}
          onPress={(e: any) => {
            e?.stopPropagation?.();
            setAgree(!agree);
          }}
          pressStyle={{ opacity: 0.75 }}
          style={{ cursor: "pointer" }}
        >
          <Stack
            width={20}
            height={20}
            borderRadius={6}
            borderWidth={2}
            borderColor={agree ? "#fff" : "rgba(255,255,255,0.45)"}
            backgroundColor={agree ? "#fff" : "transparent"}
            ai="center"
            jc="center"
            mt={1}
          >
            {agree ? <RiCheckLine size={13} color={colors.primary} /> : null}
          </Stack>
          <Text flex={1} fontSize={12.5} color="#fff" lineHeight={18} fontWeight="600">
            我同意訂單送達後，以此卡自動扣款
          </Text>
        </XStack>

        <Stack
          className={`gas-pay-bind-submit${canBind ? " ready" : ""}`}
          mt={12}
          ai="center"
          jc="center"
          onPress={(e: any) => {
            e?.stopPropagation?.();
            if (canBind) bind();
          }}
          pressStyle={{ opacity: canBind ? 0.88 : 1 }}
          style={{ cursor: canBind ? "pointer" : "default" }}
        >
          <Text fontSize={14} fontWeight="800" textAlign="center" color={canBind ? INK : "rgba(15,26,53,0.4)"}>
            {submitting ? "處理中…" : !sdkReady ? "載入安全欄位…" : "確認綁定"}
          </Text>
        </Stack>
      </div>
    </div>
  );
};

export default InlineBindCardForm;
